import { useState, useEffect, type KeyboardEvent, useRef, useMemo } from 'react';
import { Box, Drawer, Typography, Button, Select, MenuItem, FormControl, InputLabel, TextField, IconButton, Divider, Popover, FormControlLabel, Checkbox, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import SyncIcon from '@mui/icons-material/Sync';
import { GenericOption } from './FilterMultiSelect';
import { TimeRangeValue } from './constants';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SearchableSelectDropdown from 'components/Dropdown/MultiFilterPanel/SearchableSelectDropdown';
import { DEFAULT_FILTERS, FILTER_RELATIONSHIP_FIELD_MAP, FilterOptionsOps, FilterStateOps, getCascadingFilterOptions, getDerivedAssignmentGroups, reconcileCascadingFilters } from '../hooks/useUnifiedIncidents';
import React from 'react';
import AddIcon from '@mui/icons-material/Add';

type FilterKeys = Exclude<keyof FilterStateOps, 'timeRange' | 'startDate' | 'endDate'>;

export interface AdvancedFilterDrawerOpsProps {
  open: boolean;
  onClose: () => void;
  onApply: (filters: FilterStateOps) => void;
  onSaveDefault: (filters: FilterStateOps) => void;
  onResetDefault: (filters: FilterStateOps) => void;
  currentFilters: FilterStateOps;
  filterOptions: FilterOptionsOps;
  setFilterOptions: (filters: FilterOptionsOps) => void;
  originalfilterOptions: FilterOptionsOps;
  activeTile?: string;
  activeParentTile: string;
  filterRelationshipRecords: any[]
}


const SERVICE_TILE_STATUS: Record<string, string> = {
  Open: 'Open',
  OPE: 'Open',
  Inprogress: 'Work in Progress',
  'In Progress': 'Work in Progress',
  Pending: 'Pending Approval',
  'Closed Complete': 'Closed Complete',
  'Closed Incomplete': 'Closed Incomplete',
};

const LOCKED_FILTER_BY_TILE: Record<string, { field: keyof FilterStateOps; value: string }> = {
  'Closed Incidents': { field: 'incident_state', value: 'Closed' },
  Closed: { field: 'change_state', value: 'Closed' },
  P3: { field: 'priority', value: '3 - Moderate' },
  P4: { field: 'priority', value: '4 - Standard' },
  P5: { field: 'priority', value: '5 – Regular' },
  'SLA Miss': { field: 'made_sla', value: 'False' },
  Open: { field: 'service_state', value: 'Open' },
  'In Progress': { field: 'service_state', value: 'Work in Progress' },
  Pending: { field: 'service_state', value: 'Pending Approval' },
  'Closed Complete': { field: 'service_state', value: 'Closed Complete' },
  'Closed Incomplete': { field: 'service_state', value: 'Closed Incomplete' },
};

const LOCKED_PROBLEM_FILTER_BY_TILE: Record<string, { field: keyof FilterStateOps; values: string[] }> = {
  Open: { field: 'problem_state', values: ['Fix in Progress', 'Root Cause Analysis', 'Assess', 'New'] },
  'Known Errors': { field: 'known_error', values: ['True'] },
  Reactive: { field: 'u_source', values: ['Reactive'] },
  'Made SLA': { field: 'made_sla', values: ['True'] },
  RCA: { field: 'problem_state', values: ['Root Cause Analysis'] },
};

const LOCKED_CHANGE_FILTER_BY_TILE: Record<string, { field: keyof FilterStateOps; values: string[] }> = {
  Emergency: { field: 'type', values: ['Emergency'] },
  'In Progress': { field: 'change_state', values: ['Assess', 'Authorize', 'Scheduled', 'Implement', 'Review'] },
  'Not Closed': { field: 'change_state', values: ['Assess', 'Authorize', 'Scheduled', 'Implement', 'Review', 'New'] },
  Successful: { field: 'close_code', values: ['Successful'] },
  Unsuccessful: { field: 'close_code', values: ['Unsuccessful'] },
  'SLA Met': { field: 'made_sla', values: ['True'] },
  'SLA Missed': { field: 'made_sla', values: ['False'] },
};

const AdvanceFilterOpsDrawer = ({
  open,
  onClose,
  onApply,
  onSaveDefault,
  onResetDefault,
  currentFilters,
  filterOptions,
  setFilterOptions,
  originalfilterOptions,
  activeTile,
  activeParentTile,
  filterRelationshipRecords
}: AdvancedFilterDrawerOpsProps) => {
  const theme = useTheme();
  const [filters, setFilters] = useState<FilterStateOps>(currentFilters);
  const [moreFilterAnchor, setMoreFilterAnchor] = useState<null | HTMLElement>(null);
  const [selectedMoreFilters, setSelectedMoreFilters] = useState<FilterKeys[]>(
    []
  );
  const assignmentGroupsAreDerivedRef = useRef(false);
  const previousAssignmentGroupSourceRef = useRef<string | null>(null);

  const isMiscellaneousSelected =
    filters?.sector?.some(
      item => item.value === 'Miscellaneous'
    ) ?? false;
  // Sync filters when drawer opens with current applied filters
  useEffect(() => {
    if (open) {
      // Service Request tiles show their own status in the Service Request State filter
      const tileStatus = activeParentTile === 'Service Requests' && activeTile
        ? SERVICE_TILE_STATUS[activeTile]
        : undefined;
      setFilters(tileStatus
        ? { ...currentFilters, service_state: [{ label: tileStatus, value: tileStatus }] }
        : currentFilters);
      assignmentGroupsAreDerivedRef.current = false;
      previousAssignmentGroupSourceRef.current = null;
    }
  }, [open, currentFilters, activeTile, activeParentTile]);

    const toAGOptions = (values: any, key?: string): GenericOption[] => {
    return values?.map((v: any) => {
      return {label: v.assignmentGroup, value: v.assignmentGroup, disabled: !v.pipelineAlert && !v.dataQualityAlert}
    }) || [];
  };

  const getSelectedOptions = (options: GenericOption[], value: string[] | string | null) => {
    const selectedValues = Array.isArray(value) ? value : value ? [value] : [];

    return options.filter(option => !option.disabled && selectedValues.includes(option.value));
  };

  const miscellaneousSectorValues =
    filterOptions?.sectorDropdownValues?.Miscellaneous || [];

  useEffect(() => {
    if (!isMiscellaneousSelected) {
       setFilters(prev => {
        if (!prev.miscellaneousSector?.length) {
          return prev;
        }

        return {
          ...prev,
          miscellaneousSector: []
        };
      });

      return;
    }

    const options = miscellaneousSectorValues.map(v => ({
      label: v,
      value: v
    }));

    setFilters(prev => {
      if (
        prev.miscellaneousSector &&
        prev.miscellaneousSector?.length > 0
      ) {
        return prev;
      }

      return {
        ...prev,
        miscellaneousSector: options
      };
    });
  }, [
    isMiscellaneousSelected,
    miscellaneousSectorValues
  ]);

  const toOptions = (values: string[] | undefined, key?: keyof FilterStateOps): GenericOption[] => {
    return values?.map(v => {
      if (!v && ["service_offering", "cmdb_ci"].includes(key as string)) {
        const label = key === "service_offering" ? "SO" : "CI";
        return { label: `${label} not available`, value: v }
      }
      return { label: v, value: v };
    }).filter(
    option =>
      option.label !== null &&
      option.label !== undefined &&
      option.label !== '' &&
      option.value !== null &&
      option.value !== undefined &&
      option.value !== ''
  ) || [];
  };

  const withCurrentDateRange = (baseFilters: FilterStateOps): FilterStateOps => {
    const sessionTimeRange = sessionStorage.getItem('unifiedAlertsDateRangeType') as TimeRangeValue | null;
    const selectedTimeRange = sessionTimeRange || baseFilters.timeRange;
    const startDate = sessionStorage.getItem('unifiedAlertsStartDate');
    const endDate = sessionStorage.getItem('unifiedAlertsEndDate');

    if (selectedTimeRange === 'custom' && startDate && endDate) {
      return {
        ...baseFilters,
        timeRange: 'custom' as TimeRangeValue,
        startDate,
        endDate,
      };
    }

    return {
      ...baseFilters,
      timeRange: selectedTimeRange,
      startDate: undefined,
      endDate: undefined,
    };
  };

  const buildFiltersForSubmit = (): FilterStateOps => withCurrentDateRange(filters);

  const handleApply = () => {
    onApply(buildFiltersForSubmit());
    onClose();
  };

  const handleSaveDefault = () => {
    onSaveDefault(buildFiltersForSubmit());
    onClose();
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleApply();
    }
  };

  const handleReset = () => {
    assignmentGroupsAreDerivedRef.current = false;
    previousAssignmentGroupSourceRef.current = null;
    const clearedFilters = withCurrentDateRange(DEFAULT_FILTERS);
    setFilters(clearedFilters);
    onApply(clearedFilters);
  };

  const handleResetDefault = () => {
    assignmentGroupsAreDerivedRef.current = false;
    previousAssignmentGroupSourceRef.current = null;
    onResetDefault(DEFAULT_FILTERS);

    setFilters(DEFAULT_FILTERS);
    onApply(DEFAULT_FILTERS);

    onClose();
  }

  const handleMoreFilter = (event: React.MouseEvent<HTMLElement>) => {
    setMoreFilterAnchor(event.currentTarget);
  };

  const handleCloseMoreFilter = () => {
    setMoreFilterAnchor(null);
  };

  const handleMoreFilterChange = (filter: FilterKeys) => {
    setSelectedMoreFilters(prev =>
      prev.includes(filter)
        ? prev.filter(item => item !== filter)
        : [...prev, filter]
    );
  };

  const isMoreFilterOpen = Boolean(moreFilterAnchor);

  const handleClose = () => {
    // Discard temp changes
    setFilters(currentFilters);
    assignmentGroupsAreDerivedRef.current = false;
    previousAssignmentGroupSourceRef.current = null;
    onClose();
  };

  const COMMON_FILTERS: (keyof FilterStateOps)[] = [
    'department',
    'capability',
    'sector',
    'domain',
    'program',
    'vendor',
    'team',
    'assignment_group',
    'service_offering',
    'cmdb_ci'
  ];

   const MORE_FILTERS: FilterKeys[] = [
    'businessUnit',
    'technology',
    'subCapability',
    'assignmentGroupListName',
    // 'state', // Already repeated in Tile Specific Filters
    'severity',
    // 'priority', // Already repeated in Tile Specific Filters
    // 'u_source' // Already repeated in Tile Specific Filters
  ];

  const tileFilterConfig: Record<string, (keyof FilterStateOps)[]> = {
    P3: ['incident_state', 'priority', 'urgency', 'contact_type', 'made_sla', 'isDandAIAG', ...COMMON_FILTERS],
    P4: ['incident_state', 'priority', 'urgency', 'contact_type', 'made_sla', 'isDandAIAG', ...COMMON_FILTERS],
    P5: ['incident_state', 'priority', 'urgency', 'contact_type', 'made_sla', 'isDandAIAG', ...COMMON_FILTERS],
    ['Closed Incidents']: ['incident_state', 'priority', 'urgency', 'contact_type', 'made_sla', 'isDandAIAG', ...COMMON_FILTERS],
    ['SLA Miss']: ['incident_state', 'priority', 'urgency', 'contact_type', 'made_sla', 'isDandAIAG', ...COMMON_FILTERS],
    Incidents: ['incident_state', 'priority', 'urgency', 'contact_type', 'made_sla', 'isDandAIAG', ...COMMON_FILTERS],
    ['Problem Tickets']: ['problem_state', 'known_error', 'u_source', 'made_sla', 'isDandAIAG', ...COMMON_FILTERS],
    RCA: ['problem_state', 'known_error', 'u_source', 'made_sla', 'isDandAIAG', ...COMMON_FILTERS],
    Open: activeParentTile === 'Service Requests'
      ? ['service_state', 'isDandAIAG', ...COMMON_FILTERS]
      : ['problem_state', 'known_error', 'u_source', 'made_sla', 'isDandAIAG', ...COMMON_FILTERS],
    Reactive: ['problem_state', 'known_error', 'u_source', 'made_sla', 'isDandAIAG', ...COMMON_FILTERS],
    ['Known Errors']: ['problem_state', 'known_error', 'u_source', 'made_sla', 'isDandAIAG', ...COMMON_FILTERS],
    ['Made SLA']: ['problem_state', 'known_error', 'u_source', 'made_sla', 'isDandAIAG', ...COMMON_FILTERS],
    ['Service Requests']: ['service_state', 'isDandAIAG', ...COMMON_FILTERS],
    ['In Progress']: activeParentTile === 'Change Requests'
      ? ['change_state', 'type', 'close_code', 'made_sla', 'isDandAIAG', ...COMMON_FILTERS]
      : ['service_state', 'isDandAIAG', ...COMMON_FILTERS],
    ['Pending']: ['service_state', 'isDandAIAG', ...COMMON_FILTERS],
    ['Closed Complete']: ['service_state', 'isDandAIAG', ...COMMON_FILTERS],
    ['Closed Incomplete']: ['service_state', 'isDandAIAG', ...COMMON_FILTERS],
    ['Change Requests']: ['change_state', 'type', 'close_code', 'made_sla', 'isDandAIAG', ...COMMON_FILTERS],
    ['Closed']: ['change_state', 'type', 'close_code', 'made_sla', 'isDandAIAG', ...COMMON_FILTERS],
    ['Not Closed']: ['change_state', 'type', 'close_code', 'made_sla', 'isDandAIAG', ...COMMON_FILTERS],
    ['SLA Met']: ['change_state', 'type', 'close_code', 'made_sla', 'isDandAIAG', ...COMMON_FILTERS],
    ['SLA Missed']: ['change_state', 'type', 'close_code', 'made_sla', 'isDandAIAG', ...COMMON_FILTERS],
    ['Emergency']: ['change_state', 'type', 'close_code', 'made_sla', 'isDandAIAG', ...COMMON_FILTERS],
    ['Successful']: ['change_state', 'type', 'close_code', 'made_sla', 'isDandAIAG', ...COMMON_FILTERS],
    ['Unsuccessful']: ['change_state', 'type', 'close_code', 'made_sla', 'isDandAIAG', ...COMMON_FILTERS],
  };

  const activeFilters = tileFilterConfig[activeTile!] || [];
  const activeTileFilters = activeFilters.filter(filterKey => !COMMON_FILTERS.includes(filterKey));

  const getTileSectionTitle = () => {
    if (activeParentTile === 'Service Requests' || activeTile === 'Service Requests') return 'Service Request Filters';
    if (activeParentTile === 'Change Requests' || activeTile === 'Change Requests') return 'Change Request Filters';
    if (activeParentTile === 'Problem Tickets' || activeTile === 'Problem Tickets') return 'Problem Ticket Filters';
    return 'Incident Filters';
  };

  const FILTER_CONFIG: Record<FilterKeys, { label: string; pluralLabel?: string; placeholder?: string }> = {
    department: { label: "Department" },
    capability: { label: "Capability" },
    team: { label: "Team" },
    subCapability: { label: "Sub Capability" },
    vendor: { label: "Vendor" },
    technology: { label: "Technology" },
    severity: { label: "Severity" },
    incident_state: { label: "Incident State" },
    priority: { label: "Priority" },
    problem_state: { label: "Problem State" },
    service_state: { label: "Service Request State" },
    change_state: { label: "Change Request State" },
    made_sla: { label: "SLA State" },
    resolution_slaHasBreached: { label: 'SLA Resolution Miss'},
    response_slaHasBreached: { label: 'SLA Response Miss'},
    isDandAIAG: { label: "D&AI" },
    known_error: { label: "Known Error", pluralLabel: "Known Error State" },
    u_source: { label: "Source" },
    assignment_group: { label: "Assignment Group" },
    assignmentGroupListName: { label: "Assignment Group List" },
    service_offering: { label: "Service Offering" },
    businessUnit: { label: "Business Unit" },
    cmdb_ci: { label: "Configuration Item" },
    domain: { label: "Domain" },
    program: { label: "Program" },
    sector: { label: "Sector" },
    type: { label: "Type" },
    close_code: { label: "Close Code" },
    state: { label: "State" },
    urgency: { label: "Urgency" },
    contact_type: { label: "Channel" },
    miscellaneousSector: { label: "Miscellaneous Sector" },
    additionalFilters: { label: "Additional Filter" },
  };

  const handleAddCustomFilter = () => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

    setFilters(prev => ({
      ...prev,
      additionalFilters: [
        ...(prev.additionalFilters ?? []),
        { id, field: '', value: '' }
      ]
    }));
  };

  const handleUpdateCustomFilter = (id: string, changes: Partial<{ field: string; value: string }>) => {
    setFilters(prev => ({
      ...prev,
      additionalFilters: prev.additionalFilters?.map((f) =>
        f.id === id ? { ...f, ...changes } : f
      )
    }));
  };

  const handleRemoveCustomFilter = (id: string) => {
    setFilters(prev => ({
      ...prev,
      additionalFilters: prev.additionalFilters?.filter((f) => f.id !== id)
    }));
  };

  const customFilterFields = [
    { label: "Created By", value: "sys_created_by" },
    { label: "Assigned To", value: "assigned_to" },
    { label: "Location", value: "location" },
    { label: "Incident ID", value: "number" },
    { label: "Close Notes", value: "close_notes" },
    { label: "Comments", value: "comments" },
    { label: "Description", value: "description" },
    { label: "Close Code", value: "u_close_code" },
    { label: "Platform Name", value: "platformName" },
    { label: "Closed By", value: "closed_by" },
    { label: "Short Description", value: "short_description" },
    { label: "System ID", value: "sys_id" },
    { label: "Close Code (Legacy)", value: "close_code" },
    { label: "Business Service", value: "business_service" },
    { label: "Subcategory", value: "subcategory" },
    { label: "Category", value: "category" },
    { label: "Resource", value: "resource" },
    { label: "Resolved By", value: "resolved_by" },
    { label: "Updated By", value: "sys_updated_by" },
    { label: "Opened By", value: "opened_by" }
  ];

  const setKey = (
  key: keyof FilterStateOps,
  value: string[] | string | null
) => {
  const selectedValues = Array.isArray(value)
    ? value
    : value
      ? [value]
      : [];

  let options: GenericOption[] = [];

  /*
   * ------------------------------------------------------------
   * Existing cascading-options logic
   * DO NOT CHANGE
   * ------------------------------------------------------------
   */
  if (key === 'miscellaneousSector') {
    options = toOptions(
      filterOptions?.sectorDropdownValues?.Miscellaneous
    );
  } else if (key === 'isDandAIAG') {
    options = toOptions(['Yes', 'No']);
  } else {
    const filterValues = filterOptions[
      key as keyof FilterOptionsOps
    ] as string[] | undefined;

    options = toOptions(
      getCascadingOptions(
        key as keyof typeof FILTER_RELATIONSHIP_FIELD_MAP,
        filterValues || []
      ),
      key
    );
  }

  /*
   * ------------------------------------------------------------
   * Assignment Group
   *
   * Existing behavior:
   * Assignment Group -> Service Offering
   *
   * DO NOT CHANGE
   * ------------------------------------------------------------
   */
  if (key === 'assignment_group') {
    if (selectedValues.length === 0) {
      setFilterOptions({
        ...filterOptions,
        service_offering:
          originalfilterOptions?.service_offering
      });
    } else {
      const serviceOfferings = Array.from(
        new Set(
          selectedValues.flatMap(
            group =>
              filterOptions?.serviceOfferingMap?.[group] || []
          )
        )
      );

      setFilterOptions({
        ...filterOptions,
        service_offering: serviceOfferings
      });
    }
  }

  /*
   * ------------------------------------------------------------
   * Relationship:
   *
   * Assignment Group List
   * Vendor
   * Team
   *        |
   *        v
   * Assignment Group
   *
   * filterRelationshipRecords is the ONLY source for this
   * relationship.
   * ------------------------------------------------------------
   */
  if (
    key === 'assignmentGroupListName' ||
    key === 'vendor' ||
    key === 'team' ||
    key === 'subCapability' ||
    key === 'technology'
  ) {
    /*
     * Map frontend filter keys to relationship-record keys.
     */
    const relationshipFieldMap = {
      assignmentGroupListName: 'Assignment Group List',
      vendor: 'Vendor',
      team: 'Team',
      subCapability: 'Sub_Track',
      technology: 'Technology'
    } as const;

    /*
     * Build the selected values for ALL THREE relationship
     * fields.
     *
     * Start from previous filters, then replace the field
     * currently being changed with selectedValues.
     *
     * This is important because:
     *
     *   Vendor = Infosys
     *   Team   = L1
     *
     * and then selecting another Assignment Group List must
     * consider BOTH existing selections.
     */
    const relationshipSelections = {
      assignmentGroupListName:
        key === 'assignmentGroupListName'
          ? selectedValues
          : (filters.assignmentGroupListName || []).map(
              option => option.value
            ),

      vendor:
        key === 'vendor'
          ? selectedValues
          : (filters.vendor || []).map(
              option => option.value
            ),

      team:
        key === 'team'
          ? selectedValues
          : (filters.team || []).map(
              option => option.value
            ),

       subCapability:
        key === 'subCapability'
          ? selectedValues
          : (filters.subCapability || []).map(
              option => option.value
            ),

      technology:
        key === 'technology'
          ? selectedValues
          : (filters.technology || []).map(
              option => option.value
            )
    };

    /*
     * Find relationship records matching ALL selected
     * relationship fields.
     *
     * Within the same field:
     *     OR
     *
     * Across different fields:
     *     AND
     *
     * Example:
     *
     * Vendor = [Infosys, TCS]
     * Team   = [L1]
     *
     * means:
     *
     * (Vendor === Infosys OR Vendor === TCS)
     * AND
     * (Team === L1)
     */
    const matchingRecords = filterRelationshipRecords.filter(
      record => {
        return (
          (
            relationshipSelections.assignmentGroupListName.length === 0 ||
            relationshipSelections.assignmentGroupListName.includes(
              record[relationshipFieldMap.assignmentGroupListName]
            )
          ) &&
          (
            relationshipSelections.vendor.length === 0 ||
            relationshipSelections.vendor.includes(
              record[relationshipFieldMap.vendor]
            )
          ) &&
          (
            relationshipSelections.team.length === 0 ||
            relationshipSelections.team.includes(
              record[relationshipFieldMap.team]
            )
          ) &&
          (
            relationshipSelections.subCapability.length === 0 ||
            relationshipSelections.subCapability.includes(
              record[relationshipFieldMap.subCapability]
            )
          ) &&
          (
            relationshipSelections.technology.length === 0 ||
            relationshipSelections.technology.includes(
              record[relationshipFieldMap.technology]
            )
          )
        );
      }
    );

    /*
     * Get the Assignment Groups from the matching records.
     */
    const assignmentGroups = Array.from(
      new Set(
        matchingRecords
          .map(record => record['Assignment Group'])
          .filter(
            (assignmentGroup): assignmentGroup is string =>
              Boolean(assignmentGroup)
          )
      )
    );

    /*
     * Convert them into the same GenericOption structure
     * used by assignment_group.
     */
    const assignmentGroupOptions = toOptions(
      assignmentGroups,
      'assignment_group'
    );

    /*
     * ------------------------------------------------------------
     * Service offerings
     *
     * Keep the existing behavior:
     * derived Assignment Groups -> service offerings
     * ------------------------------------------------------------
     */
    const serviceOfferings =
      assignmentGroups.length === 0
        ? originalfilterOptions?.service_offering ?? []
        : Array.from(
            new Set(
              assignmentGroups.flatMap(
                group =>
                  filterOptions?.serviceOfferingMap?.[group] || []
              )
            )
          );

    setFilterOptions({
      ...filterOptions,
      service_offering: serviceOfferings
    });

    /*
     * Update the current filter AND automatically select the
     * related Assignment Groups.
     */
    setFilters(prev => ({
      ...prev,

      /*
       * Auto-selected Assignment Groups based on the
       * combination of Vendor / Team / Assignment Group List.
       */
      assignment_group: assignmentGroupOptions,

      /*
       * Preserve the actual selection for the field that
       * triggered this update.
       */
      [key]: options.filter(option =>
        selectedValues.includes(option.value)
      )
    }));

    return;
  }

  /*
   * ------------------------------------------------------------
   * Everything else remains exactly as before.
   * ------------------------------------------------------------
   */
  setFilters(prev => ({
    ...prev,
    [key]: options.filter(option =>
      selectedValues.includes(option.value)
    )
  }));
};
  const renderFilter = (key: keyof FilterStateOps) => {

    let config = FILTER_CONFIG[key as FilterKeys]

    if (!config) return null;

    const isDandAI = key === 'isDandAIAG';

    const fallback =
    isDandAI
      ? ['Yes', 'No']
      : (filterOptions[key as keyof FilterOptionsOps] as string[]) || [];

    const tileLock = activeTile ? LOCKED_FILTER_BY_TILE[activeTile] : undefined;
    const lockedStatus = tileLock?.field === key ? tileLock.value : undefined;
    const problemLock = activeParentTile === 'Problem Tickets' && activeTile
      ? LOCKED_PROBLEM_FILTER_BY_TILE[activeTile]
      : undefined;
    const changeLock = activeParentTile === 'Change Requests' && activeTile
      ? LOCKED_CHANGE_FILTER_BY_TILE[activeTile]
      : undefined;
    const lockedValues = problemLock?.field === key
      ? problemLock.values
      : changeLock?.field === key
        ? changeLock.values
        : lockedStatus ? [lockedStatus] : undefined;

    const options = lockedValues
      ? toOptions(lockedValues, key)
      : toOptions(
        isDandAI
          ? ['Yes', 'No']
          : getCascadingOptions(
            key as keyof typeof FILTER_RELATIONSHIP_FIELD_MAP,
            fallback
          ),
        key
      );
    const value = filters[key as FilterKeys] || [];

    const dropdown = (
      <SearchableSelectDropdown
        key={key}
        label={config.label}
        placeholder={`Select ${config.label}...`}
        options={options}
        multiple
        value={lockedValues ?? (Array.isArray(value) ? value.map(item => item.value) : [])}
        onChange={(next) => {
          if (lockedValues) return;
          if (isDandAI) {
            const nextArr = Array.isArray(next) ? next : next ? [next] : [];
            const singleVal = nextArr.length > 0 ? [nextArr[nextArr.length - 1]] : [];
            setKey(key, singleVal);
            return;
          }
          setKey(key, next);
        }}
        disabled={Boolean(lockedValues)}
        showSearch={!isDandAI}
        showAllOption={!isDandAI}
        chipDisplay
      />
    );

    return (
      <Box
        key={key}
        sx={{
          mb: 2,
          ...(lockedValues && {
            '& .MuiPaper-root': { opacity: '1 !important' },
            '& .MuiChip-root': { width: 'auto !important', maxWidth: 'none !important' },
          }),
        }}
      >
        {lockedValues ? (
          <Tooltip title={lockedValues.join(', ')} placement="top" arrow>
            <Box>{dropdown}</Box>
          </Tooltip>
        ) : dropdown}
      </Box>
    );
  };

  const miscellaneousSectors =
  filterOptions?.sectorDropdownValues?.Miscellaneous || [];

const cascadingFilterOptions = useMemo(() => {
  return getCascadingFilterOptions(
    filters,
    filterRelationshipRecords,
    miscellaneousSectors,
    {
      ignoreDerivedAssignmentGroup:
        assignmentGroupsAreDerivedRef.current
    }
  );
}, [
  filters,
  filterRelationshipRecords,
  miscellaneousSectors
]);
  const previousCascadingFiltersRef = useRef<Record<string, string[]> | null>(null);

  // useEffect(() => {
  //   console.log('🔄 CASCADING FILTERS', {
  //     before: previousCascadingFiltersRef.current,
  //     after: cascadingFilterOptions,
  //   });

  //   previousCascadingFiltersRef.current = cascadingFilterOptions;
  // }, [cascadingFilterOptions]);

  useEffect(() => {
    if (!Object.keys(cascadingFilterOptions).length) {
      return;
    }

    const reconciledFilters = reconcileCascadingFilters(
      filters,
      cascadingFilterOptions
    );

    if (reconciledFilters !== filters) {
      // setFilters(reconciledFilters);
       setFilters({
      ...reconciledFilters,

      // Sector is independently controlled.
      // Do not allow cascading reconciliation to modify it.
      sector: filters.sector
    });
    }
  }, [
    cascadingFilterOptions,
    filters
  ]);

  const derivedAssignmentGroups = useMemo(() => {
      return getDerivedAssignmentGroups(
        filters,
        filterRelationshipRecords
      );
    }, [
      filters.vendor,
      filters.team,
      filters.subCapability,
      filters.technology,
      filters.assignmentGroupListName,
      filterRelationshipRecords
    ]);

    const assignmentGroupSourceSignature = useMemo(() => {
      const getValues = (
        options?: GenericOption[]
      ) =>
        (options || [])
          .map(option => String(option.value))
          .sort();

      return JSON.stringify({
        vendor: getValues(filters.vendor),
        team: getValues(filters.team),
        subCapability: getValues(filters.subCapability),
        technology: getValues(filters.technology),
        assignmentGroupListName: getValues(
          filters.assignmentGroupListName
        )
      });
    }, [
      filters.vendor,
      filters.team,
      filters.subCapability,
      filters.technology,
      filters.assignmentGroupListName
    ]);

    useEffect(() => {
      /*
      * Don't process the same Vendor / Team / AG List
      * combination again.
      */
      if (
        previousAssignmentGroupSourceRef.current ===
        assignmentGroupSourceSignature
      ) {
        return;
      }

      previousAssignmentGroupSourceRef.current =
        assignmentGroupSourceSignature;

      /*
      * --------------------------------------------------
      * CASE 1:
      * No Vendor / Team / AG List is selected anymore.
      * --------------------------------------------------
      *
      * If Assignment Group was previously populated
      * automatically, clear it.
      *
      * If Assignment Group was manually selected,
      * leave it alone.
      */
      if (!derivedAssignmentGroups.length) {
        if (assignmentGroupsAreDerivedRef.current) {
          assignmentGroupsAreDerivedRef.current = false;

          setFilters(prev => {
            if (!prev.assignment_group?.length) {
              return prev;
            }

            return {
              ...prev,
              assignment_group: []
            };
          });
        }

        return;
      }

      /*
      * --------------------------------------------------
      * CASE 2:
      * Vendor / Team / AG List has active selections.
      * --------------------------------------------------
      */

      const assignmentGroupOptions = toAGOptions(
        filterOptions.assignment_group || [],
        'assignment_group'
      );

      const nextAssignmentGroups = getSelectedOptions(
        assignmentGroupOptions,
        derivedAssignmentGroups
      );

      /*
      * This Assignment Group selection is generated
      * from Vendor / Team / AG List.
      */
      assignmentGroupsAreDerivedRef.current = true;

      setFilters(prev => {
        const previousValues = (prev.assignment_group || [])
          .map(option => String(option.value))
          .sort();

        const nextValues = nextAssignmentGroups
          .map(option => String(option.value))
          .sort();

        /*
        * Nothing actually changed.
        */
        if (
          previousValues.length === nextValues.length &&
          previousValues.every(
            (value, index) => value === nextValues[index]
          )
        ) {
          return prev;
        }

        return {
          ...prev,
          assignmentGroup: nextAssignmentGroups
        };
      });
    }, [
      assignmentGroupSourceSignature,
      derivedAssignmentGroups,
      filterOptions.assignment_group
    ]);

  const getCascadingOptions = (
    key: keyof typeof FILTER_RELATIONSHIP_FIELD_MAP,
    fallback: string[]
  ) => {
    if (key === 'sector') return fallback;
    return cascadingFilterOptions[key] ?? fallback;
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      variant="persistent"
      PaperProps={{
        sx: {
          width: 560,
          minWidth: 560,
          maxWidth: 560,
          backgroundColor: '#FFFFFF',
          height: 'calc(100vh - 76px)',
          top: '76px',
          overflowY: 'auto',
          boxShadow: '-2px 4px 8px rgba(0, 0, 0, 0.1)',
        }
      }}
      ModalProps={{
        BackdropProps: {
          sx: {
            backgroundColor: 'rgba(255, 255, 255, 0.5)',
          }
        }
      }}
    >
      <Box
        sx={{ display: 'flex', flexDirection: 'column', height: '100%', backgroundColor: '#FFFFFF' }}
        onKeyDown={handleKeyDown}
      >
        {/* Header */}
        <Box
          sx={{
            px: 2,
            py: 1.25,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography
            sx={{
              fontSize: 18,
              fontWeight: 500,
              color: theme.palette.filterPanel.headerTitle,
            }}
          >
            Advance Filters
          </Typography>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>

              <Button
                  variant="text"
                  onClick={handleMoreFilter}
                  startIcon={<AddIcon sx={{ fontSize: '1rem' }} />}
                  sx={{
                    color: `${theme.palette.filterPanel.clearButtonText} !important`,
                    textTransform: 'uppercase',
                    fontWeight: 500,
                    fontSize: '14px'
                  }}
                >
                  More Filters
              </Button>

              <IconButton size="small" onClick={handleClose} sx={{ '&&': { p: 0.5 } }}>
                <CloseIcon fontSize="small" sx={{ color: theme.palette.filterPanel.headerTitle }} />
              </IconButton>

          </Box>
        </Box>

        <Popover
          open={isMoreFilterOpen}
          anchorEl={moreFilterAnchor}
          onClose={handleCloseMoreFilter}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          PaperProps={{
            sx: {
              mt: 1,
              width: 220,
              borderRadius: 1.5,
              boxShadow: theme.shadows[4],
              p: 1,
            },
          }}
        >
          <Box>
           {MORE_FILTERS.map(filterKey => (
              <FormControlLabel
                key={filterKey}
                control={
                  <Checkbox
                    size="small"
                    checked={selectedMoreFilters.includes(filterKey)}
                    onChange={() => handleMoreFilterChange(filterKey)}
                  />
                }
                label={
                  <Typography
                    sx={{
                      fontSize: '14px',
                      fontWeight: 400,
                    }}
                  >
                    {FILTER_CONFIG[filterKey].label}
                  </Typography>
                }
                sx={{
                  display: 'flex',
                  width: '100%',
                  mx: 0,
                  borderRadius: 1,
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
              />
            ))}
          </Box>
        </Popover>

        <Divider sx={{ borderColor: theme.palette.filterPanel.headerTitle }} />

        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 2,
          p: 2,
          marginTop: '10px'
        }}>
          <Button
            variant="text"
            onClick={handleResetDefault}
            startIcon={<SyncIcon sx={{ fontSize: '1rem' }} />}
            sx={{
              color: `${theme.palette.filterPanel.clearButtonText} !important`,
              textTransform: 'uppercase',
              fontWeight: 500,
              fontSize: '14px'
            }}
          >
            RESET DEFAULT FILTERS
          </Button>
          <Button
            variant="outlined"
            onClick={handleSaveDefault}
            sx={{
              color: `${theme.palette.filterPanel.clearButtonText} !important`,
              borderColor: `${theme.palette.filterPanel.clearButtonText} !important`,
              textTransform: 'uppercase',
              fontWeight: 500,
              fontSize: '14px',
              '&:hover': {
                borderColor: `${theme.palette.filterPanel.clearButtonText} !important`,
              }
            }}
          >
            SAVE AS DEFAULT
          </Button>
        </Box>

        {/* Body */}
        <Box sx={{ flex: 1, overflowY: 'auto', p: 2, backgroundColor: '#FFFFFF' }}>
          <Box>
            <Box>
              {COMMON_FILTERS.map((filterKey) => (
                <React.Fragment key={filterKey}>
                  {renderFilter(filterKey)}

                  {filterKey === 'sector' && isMiscellaneousSelected && (
                    <Box sx={{ mb: 2 }}>
                      <SearchableSelectDropdown
                        label="Miscellaneous Sector"
                        placeholder="Select Miscellaneous Sector..."
                        options={toOptions(
                          filterOptions?.sectorDropdownValues?.Miscellaneous
                        )}
                        multiple
                        value={(filters.miscellaneousSector || []).map(
                          (item) => item.value
                        )}
                        onChange={(next) => setKey('miscellaneousSector', next)}
                        showSearch
                        showAllOption
                        chipDisplay
                      />
                    </Box>
                  )}
                </React.Fragment>
              ))}
              {MORE_FILTERS.map(filterKey => {
                if (!selectedMoreFilters.includes(filterKey)) {
                  return null;
                }

                return (
                  <React.Fragment key={filterKey}>
                    {renderFilter(filterKey)}
                  </React.Fragment>
                );
              })}
              {activeTileFilters.length > 0 && (
                <>
                  <Typography sx={{ fontWeight: 600, color: theme.palette.filterPanel.headerTitle, mt: 1, mb: 2 }}>
                    {getTileSectionTitle()}
                  </Typography>

                  {activeTileFilters.map(filterKey => renderFilter(filterKey))}
                </>
              )}
            </Box>
          </Box>

          <Box sx={{ py: 2, backgroundColor: '#FFFFFF' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body1" sx={{ fontWeight: 600, color: theme.palette.filterPanel.headerTitle }}>
                Additional Filters
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={handleAddCustomFilter}
                sx={{ textTransform: 'uppercase', fontWeight: 500, fontSize: '0.9rem' }}
              >
                + Add Filter
              </Button>
            </Box>

            {filters?.additionalFilters?.map((filter) => (
              <Box
                key={filter.id}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr auto',
                  gap: 1,
                  alignItems: 'center',
                  mb: 1
                }}
              >
                <FormControl fullWidth>
                  <InputLabel
                    sx={{
                      color: '#1b1d1fff !important',
                      fontWeight: 400,
                      backgroundColor: 'white',
                      paddingX: '4px',
                      marginLeft: '-2px',
                      "&.MuiInputLabel-root": {
                        color: '#333333 !important'
                      },
                      '&.Mui-focused': {
                        color: '#333333 !important',
                        backgroundColor: 'white'
                      },
                      '&.MuiInputLabel-shrink': {
                        color: '#333333 !important',
                        backgroundColor: 'white',
                        paddingX: '4px'
                      }
                    }}
                  >
                    Field
                  </InputLabel>
                  <Select
                    value={filter.field}
                    label="Select Field"
                    onChange={(e) => handleUpdateCustomFilter(filter.id, { field: e.target.value })}
                    IconComponent={KeyboardArrowDownIcon}
                    sx={{
                      height: '44px',
                      backgroundColor: `${theme.palette.filterDropdown.fieldBackground} !important`,
                      '& .MuiSelect-select': {
                        color: 'text.primary',
                        paddingTop: '10px',
                        paddingBottom: '10px'
                      },
                      '& .MuiSelect-icon': {
                        color: `${theme.palette.filterDropdown.fieldIcon} !important`
                      },
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          backgroundColor: `${theme.palette.filterDropdown.panelBackground} !important`,
                          color: `${theme.palette.filterDropdown.label} !important`,
                          '& .MuiMenu-list': {
                            backgroundColor: `${theme.palette.moreOptionDropdown.background} !important`,
                          },
                          '& .MuiMenuItem-root': {
                            backgroundColor: `${theme.palette.filterDropdown.panelBackground} !important`,
                            color: `${theme.palette.filterDropdown.label} !important`,
                            '&:hover': {
                              backgroundColor: `${theme.palette.filterDropdown.optionHoverBackground} !important`,
                              color: `${theme.palette.filterDropdown.label} !important`,
                            },
                            '&.Mui-selected': {
                              backgroundColor: `${theme.palette.filterDropdown.optionHoverBackground} !important`,
                              color: `${theme.palette.filterDropdown.label} !important`,
                            },
                            '&.Mui-selected:hover': {
                              backgroundColor: `${theme.palette.filterDropdown.optionHoverBackground} !important`,
                              color: `${theme.palette.filterDropdown.label} !important`,
                            },
                          },
                        },
                      },
                    }}
                  >
                    {customFilterFields.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  label="Value"
                  value={filter.value}
                  onChange={(e) => handleUpdateCustomFilter(filter.id, { value: e.target.value })}
                  fullWidth
                  sx={{
                    color: 'black !important',
                    backgroundColor: `${theme.palette.filterDropdown.fieldBackground} !important`,
                    '& .MuiInputBase-root': { height: '44px' },
                  }}
                  InputProps={{
                    sx: {
                      '& .MuiOutlinedInput-input': { color: `${theme.palette.text.primary} !important` }
                    }
                  }}
                  InputLabelProps={{
                    sx: {
                      color: `${theme.palette.text.primary} !important`,
                      fontWeight: 400,
                      backgroundColor: 'transparent',
                      paddingX: '4px',
                      marginLeft: '-2px',
                      "&.MuiInputLabel-root": {
                        color: '#333333 !important'
                      },
                      '&.Mui-focused': {
                        color: `${theme.palette.text.primary} !important`,
                        backgroundColor: 'white'
                      },
                      '&.MuiInputLabel-shrink': {
                        color: `${theme.palette.text.primary} !important`,
                        backgroundColor: 'white',
                        paddingX: '4px'
                      }
                    }
                  }}
                />

                <IconButton
                  onClick={() => handleRemoveCustomFilter(filter.id)}
                  sx={{
                    width: 35,
                    height: 35,
                    border: `1px solid ${theme.palette.inputNew.removeBorder} !important`,
                    borderRadius: '4px',
                    color: `${theme.palette.inputNew.removeIcon} !important`,
                    backgroundColor: '#FFFFFF !important',
                    '&:hover': {
                      backgroundColor: `${theme.palette.inputNew.removeHoverBackground} !important`,
                      border: `1px solid ${theme.palette.inputNew.removeBorder} !important`,
                    },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 16, color: `${theme.palette.inputNew.removeIcon} !important` }} />
                </IconButton>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Footer */}
        <Box
          sx={{
            p: 2,
            gap: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            bgcolor: theme.palette.filterPanel.footerBackground,
          }}
        >
          <Button
            variant="text"
            onClick={handleReset}
            sx={{
              '&&': {
                color: `${theme.palette.filterPanel.clearButtonText} !important`,
                fontWeight: 500,
                textTransform: 'uppercase',
                fontSize: 14,
                p: 0,
                minWidth: 'unset',
              },
            }}
          >
            Clear Filters
          </Button>

          <Button
            variant="contained"
            onClick={handleApply}
            sx={{
              '&&': {
                bgcolor: `${theme.palette.filterPanel.applyButtonBackground} !important`,
                '&:hover': {
                  bgcolor: `${theme.palette.filterPanel.applyButtonHoverBackground} !important`,
                },
                fontWeight: 500,
                fontSize: 14,
                textTransform: 'uppercase',
                borderRadius: '4px',
                px: 3,
                py: 1,
                minHeight: 36,
              },
            }}
          >
            Apply
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default AdvanceFilterOpsDrawer;